import { Component, OnDestroy, TemplateRef, ViewChild } from '@angular/core';
import { KitAuditService } from 'src/app/acumatica/EntityServices/KitAudit.service';
import { KitAuditNotice_InpersistentMessage, KitAuditSet, KitAuditType } from '../kit-audit';
import { Router } from '@angular/router';
import { ApplicationScreen, ScreenContextMenuItem } from 'src/app/application-navigator/application-screen';
import { MatDialog } from '@angular/material/dialog';
import { AlertDialog } from 'src/app/common/alert-dialog/alert-dialog.component';
import { ConfirmationDialog } from 'src/app/common/confirmation-dialog/confirmation-dialog.component';
import { AudioService, Sound } from 'src/app/services/audio.service';
import { KitStockComponent } from 'src/app/acumatica/EntityServices/KitSpecification.service';
import { noop } from 'rxjs';

@Component({
  selector: 'app-kit-selection',
  templateUrl: './kit-selection.component.html',
  styleUrls: ['./kit-selection.component.css']
})
export class KitAuditSelectKitComponent extends ApplicationScreen implements OnDestroy {

  /** interface to the Audit Service's kit audit operation data object */
  public get audit() { return this.kitAudit!.audit!; }

  constructor(
    private kitAudit: KitAuditService,
    private router: Router,
    private dialog: MatDialog,
    private audio: AudioService,
  ) {
    super();

    if(this.kitAudit?.audit === undefined) {
      this.router.navigate(['kit-audit']);
      return;
    }

    switch(this.audit.type) {
      case KitAuditType.Binders:
        this.LoadEmptyBinderShells();
        this.pageTitle = "Binder Card Audit";
        break;
      case KitAuditType.LooseCards: this.pageTitle = "Loose Card Audit"; break;
      case KitAuditType.CollatedSets:
        this.pageTitle = "Collated Set Memo Audit";
        this.onScannerInput.subscribe(data => this.receiveScannerInput(data));
        break;
    }
  }
    
  screenMenuItems: ScreenContextMenuItem[] = [
    {
        label: 'Refresh Binder Contents',
        icon: 'file_download',
        handler: () => { this.kitAudit.RefreshBinderContents() },
    },
    {
      icon: 'restart_alt',
      label: 'Cancel this audit / Reset',
      handler: () => {
        this.dialog.open(ConfirmationDialog, {
          data: {
            message: 'Reset all current audit details?',
            confirmText: 'Yes, Reset',
            cancelText: 'No, Cancel'
          }
        }).afterClosed().subscribe(response => {
          if (!response) return;
          this.kitAudit.audit = undefined;
          this.router.navigate(['/']);
        })
      }
    }
  ]

  ngOnDestroy(): void {
    this.onScannerInput.unsubscribe();
  }
  
  public gotoKitAudit(kit: KitAuditSet, autoCheckInventoryID?: string) {
    this.kitAudit.AuditKitContents(kit, autoCheckInventoryID);
  }

  public kitAuditProgress(kit: KitAuditSet) {
    return kit.Checked().length + ' / ' + kit.items.length;
  }

  public receiveScannerInput(inventoryID: string | object) {
    this.kitAudit.ScanValueToInventoryID(inventoryID)
      .then(inventoryID => {

        // initialize the Collated Set KitAuditSet
        this.kitAudit.InitCollatedSetAudit(inventoryID)
        .then(auditSet => {
          // check if there is already one for the scanned item's collated set
          let existingKit = this.audit?.kits.find(search => search.Description == auditSet.Description);
          // if so, set the auditSet variable to the existing one
          if(existingKit) auditSet = existingKit;
          // otherwise add the newly initialized one to the audit kits array
          else this.audit?.kits.push(auditSet);
          // go into auditing the indicated kit
          this.gotoKitAudit(auditSet, inventoryID);
        })
        .catch(err => {
          this.dialog.open(AlertDialog, {data:{message: err, buttonText: 'OK'}});
        })

      })
      .catch(errorMessage => {
        this.audio.play(Sound.ErrorBeep);
        let notice = new KitAuditNotice_InpersistentMessage("No Inventory ID could be identified in the scanned data\n" + JSON.stringify(inventoryID));
        this.kitAudit.RegisterNotice(notice);
      })

  }

  public anyItemsToOrder(): boolean {
    for(var idx=0; idx<this.audit!.kits.length; idx++)
      if(this.audit!.kits[idx].PartiallyChecked()) return true;
    return false;
  }

  // #region Empty Binder Shell special handling
  @ViewChild('emptyBinderShellSelectionTemplate') emptyBinderShellSelectionTemplate!: TemplateRef<any>

  /** provides 2-way bindable interface to the anyBinderShellsPlaced property flag */
  public get anyBinderShellsPlaced(): boolean { return this.kitAudit.anyBinderShellsPlaced; }
  public set anyBinderShellsPlaced(value: boolean) { this.kitAudit.anyBinderShellsPlaced = value; }    

  /** function invoked by component initialization to load the Binder Shells list to have ready for dialog prompt */
  private LoadEmptyBinderShells() {
    this.kitAudit.GetCurrentBinderContents()
      .then(binders => {
      // map the Sample Card Binders (as specified in config file) and create a list of the main Binder/Kit ID with the binder shell component
      this.BinderShells = this.kitAudit.SampleCardBinders!.map(binder => ({
        KitID: binder.KitID,
        EmptyShellItem: binders[binder.KitID].EmptyBinderShell!
      }));
    });
  }

  /** the array of Empty Binder Shell KitStockComponent objects */
  public BinderShells?: {KitID: string, EmptyShellItem: KitStockComponent}[];

  /** dialog prompts the user to specify which empty binder shells are being placed */
  public promptForBinderShellsPlaced(): Promise<void> {
    if(!this.anyBinderShellsPlaced) return Promise.resolve();

    return new Promise<void>((resolve, reject) => {
      this.dialog.open(this.emptyBinderShellSelectionTemplate).afterClosed().subscribe(response => {
        if(response) {
          this.kitAudit.Persist();
          resolve();
        }
        else reject();
      });
    });
  }

  // #endregion

  public createMissingItemOrder() {
    this.promptForBinderShellsPlaced().then(() => {
      this.kitAudit.CreateMissingComponentOrder();
    })
    .catch(noop /* user cancelled out of prompt for empty binder shells */);
  }

  public anyBindersAudited(): boolean {
    return this.audit.AnyChecked() && this.audit.IsBinderAudit();
  }

  public updateCustomerSampleBooks() {
    this.promptForBinderShellsPlaced().then(() => {

    })
    .catch(noop /* user cancelled out of prompt for empty binder shells */);
  }
}
